/**
 * Workspace-level sqlite3mt provider -- the integration adapter that wires
 * the existing workspace transport/crypt/crypto layer into the sqlite3mt
 * runtime bridge.
 *
 * This is the hardest remaining integration point: the single seam where
 * all 30+ sqlite3mt modules meet the existing SDK. The factory returned by
 * `createSqlite3MtWorkspaceProvider` is passed as
 * `SdkConfig.createSqliteEngineFactory` and flows through:
 *
 *   SdkConfig -> openSdkWorkspace -> RealWorkspace ->
 *   resolveWorkspaceSqliteEngineFactory -> this provider ->
 *   createSqlite3MtEngineFactory -> sqlite3mt runtime
 *
 * The provider constructs the object-store-facing
 * Sqlite3MtLifecycleRuntimeBridgeDeps from workspace-level deps.
 * Its public-head transport uses a runtime-specific head carrier so sqlite3mt
 * can keep a separate linear public history without overwriting the normal
 * workspace commit chain.
 */

import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import type { BlobRepository } from './blobRepository'
import {
    buildSignedCommit,
    extractVersionFromCommit,
} from './commitSigning'
import type { CommitRecord } from './commitSigning'
import { SdkError } from './errors'
import { sdkConsole, type SdkLoggingOptions } from './logger'
import type { SdkObservers } from './observers'
import type { CryptoProvider, WorkspaceSyncPolicy } from './types'
import type {
    SqliteEngineFactory,
    WorkspaceSqliteEngineFactoryContext,
} from './sqliteEngine'
import {
    createSqlite3MtEngineFactory,
    createSqlite3MtLifecycleRuntimeBridge,
} from './sqlite3mt/engine-factory'
import type {
    CreateSqlite3MtRuntimeArgs,
    Sqlite3MtLifecycleRuntimeBridgeDeps,
    Sqlite3MtRuntimeBridge,
    Sqlite3WasmRuntimeApi,
} from './sqlite3mt/engine-factory'
import { buildManifestOperations } from './sqlite3mt/manifest-ops'
import {
    createEmptyPublicRoot,
    decodePublicRootV2Payload,
    encodePublicRootV2PayloadFromRoot,
    type Sqlite3mtPublicRootV2Payload,
} from './sqlite3mt/public-manifest'
import type {
    FullManifestOperations,
    ManifestOpsDeps,
    PublishResult,
    SecureBlobRpcBridge,
} from './sqlite3mt/manifest-ops'
import type { DurableLocalStore } from './sqlite3mt/durable'
import type { HashPageFn } from './sqlite3mt/wal-scan'
import { sameRoot, type PageHash, type Root } from './sqlite3mt/types'
import { emitSecureBlobComponentProgress } from './componentRuntimeProgress'

// -- Workspace-level sqlite3mt deps ----------------------------------------

/**
 * Dependencies that the workspace layer must provide to construct a
 * sqlite3mt runtime bridge.
 *
 * These map directly to workspace-level APIs:
 * - `repository` -> `RealWorkspace.deps.repository` (BlobRepository)
 * - `crypto` -> `RealWorkspace.crypto` (WebCryptoProvider)
 * - `stateKey` -> `RealWorkspace.stateKey` (encryption key)
 * - `blobId` -> `RealWorkspace.blobId`
 * - `durable` -> constructed locally (InMemory or IndexedDB store)
 */
export interface Sqlite3MtWorkspaceProviderDeps {
    /** Blob repository for CAS object access. */
    readonly repository: BlobRepository
    /** Crypto provider for SHA-256, encryption, etc. */
    readonly crypto: CryptoProvider
    /** Encryption key derived from workspace state. */
    readonly stateKey: Uint8Array
    /** Blob ID for head management. */
    readonly blobId: string
    /** Optional SDK-wide observers forwarded into sqlite3mt runtime state. */
    readonly observers?: SdkObservers
    /** Optional console logging control forwarded into sqlite3mt runtime state. */
    readonly logging?: SdkLoggingOptions
    /** Current workspace sync policy. Defaults to `on-demand` when omitted. */
    readonly getSyncPolicy?: () => WorkspaceSyncPolicy
    /** Shared durable local store instance for exactly one sqlite database runtime. */
    readonly durable?: DurableLocalStore
    /**
     * Create a durable local store for a concrete sqlite database runtime.
     *
     * Browser and desktop clients can host multiple `.db` files and multiple
     * live runtime instances inside the same workspace. Reusing one durable
     * journal/snapshot store across those local runtimes corrupts bootstrap by
     * replaying another runtime's WAL-local state.
     */
    readonly createDurableLocalStore?: (args: {
        readonly workspace: string
        readonly headTransport: Sqlite3MtWorkspaceHeadTransport
    }) => DurableLocalStore
    /** Load SQLite WASM runtime. */
    readonly loadSqlite3: () => Promise<Sqlite3WasmRuntimeApi>
    /** Page size in bytes. Default: 4096. */
    readonly pageSize?: number
    /** Initial WAL shadow bytes allocated in SAB2. */
    readonly initialWalSize?: number
    /** Maximum WAL shadow bytes reserved in SAB2 for one commit. */
    readonly maxWalSize?: number
    /** Bounded read-ahead pages after sequential sqlite3mt demand. Default: 8. */
    readonly faultReadAheadPages?: number
    /**
     * Resolve the runtime-scoped public-head transport for a concrete SQLite db.
     *
     * Defaults to using `config.workspace` unchanged, which preserves the
     * existing `${blobId}:${path}` path carrier from the session layer.
     */
    readonly resolveHeadTransport?: (workspace: string) => Sqlite3MtWorkspaceHeadTransport
    /**
     * Called when the sqlite3mt sync engine is ready for a database.
     *
     * The callback receives the runtime-scoped sqlite3mt head key and a
     * `triggerSync` function that kicks the internal
     * push/reconcile cycle. This is used by the workspace to register WS
     * notification routing so that remote head changes trigger immediate sync.
     */
    readonly onSyncEngineReady?: (
        headBlobId: string,
        triggerSync: () => void,
        options?: { backgroundSync?: 'workspace-policy' | 'manual' },
    ) => void | (() => void)
    /**
     * When true, the engine factory skips opening a concrete runtime-owner
     * connection and returns a lightweight handle backed only by the shared
     * sqlite3mt runtime state. This is required in browser control workers,
     * where a same-thread runtime-owner would deadlock on async page faults.
     */
    readonly virtualRuntimeOwner?: boolean
}

function getWorkspaceSyncPolicy(deps: Sqlite3MtWorkspaceProviderDeps): WorkspaceSyncPolicy {
    return deps.getSyncPolicy?.() ?? 'on-demand'
}

function isWorkspaceSyncPaused(deps: Sqlite3MtWorkspaceProviderDeps): boolean {
    return getWorkspaceSyncPolicy(deps) === 'paused'
}

function assertSqlite3MtSyncAllowed(deps: Sqlite3MtWorkspaceProviderDeps, operation: string): void {
    if (isWorkspaceSyncPaused(deps)) {
        throw new Error(`sqlite3mt sync paused by workspace policy during ${operation}`)
    }
}

/**
 * Runtime-scoped transport inputs for sqlite3mt's public head.
 *
 * `headBlobId` is a runtime-local sync key used for durable-store partitioning
 * and notification routing. `headLane` identifies the sqlite3mt public-head lane
 * within the workspace blob. `writerSeed` is reserved for the signed commit bridge.
 */
export interface Sqlite3MtWorkspaceHeadTransport {
    readonly headBlobId: string
    readonly headLane?: string
    readonly writerSeed?: Uint8Array
}

const DEFAULT_SQLITE3MT_TRANSPORT_TIMEOUT_MS = 180_000
const MAX_SQLITE3MT_TRANSPORT_TIMEOUT_MS = 900_000

function sqlite3mtObjectMissingPublishError(message: string): SdkError {
    return new SdkError(
        'OBJECT_MISSING',
        `sqlite3mt publish object missing after upload: ${message}`,
        { retryable: true },
    )
}

function readSqlite3MtTransportTimeoutMs(): number {
    const processLike = globalThis as typeof globalThis & {
        __secureblobComponentJobRuntimeEnv?: Record<string, string | undefined>
        process?: { env?: Record<string, string | undefined> }
    }
    const value = processLike.__secureblobComponentJobRuntimeEnv?.SECUREBLOB_SQLITE3MT_TRANSPORT_TIMEOUT_MS
        ?? processLike.process?.env?.SECUREBLOB_SQLITE3MT_TRANSPORT_TIMEOUT_MS
    if (value == null || value.trim().length === 0) {
        return DEFAULT_SQLITE3MT_TRANSPORT_TIMEOUT_MS
    }
    const parsed = Number(value)
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= MAX_SQLITE3MT_TRANSPORT_TIMEOUT_MS) {
        return Math.trunc(parsed)
    }
    sdkConsole.warn('[sqlite3mt:transport] ignoring invalid transport timeout override', {
        value,
        fallback: DEFAULT_SQLITE3MT_TRANSPORT_TIMEOUT_MS,
    })
    return DEFAULT_SQLITE3MT_TRANSPORT_TIMEOUT_MS
}

function withSqlite3MtTransportTimeout<T>(
    promise: Promise<T>,
    label: string,
    timeoutMs = readSqlite3MtTransportTimeoutMs(),
): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timeout = setTimeout(() => {
            reject(new SdkError(
                'NETWORK_ERROR',
                `sqlite3mt transport timed out after ${timeoutMs}ms: ${label}`,
                { retryable: true },
            ))
        }, timeoutMs)

        promise.then(
            value => {
                clearTimeout(timeout)
                resolve(value)
            },
            error => {
                clearTimeout(timeout)
                reject(error)
            },
        )
    })
}

// -- Helper: buildAad for sqlite3mt objects --------------------------------

function sqlite3mtAad(blobId: string, scope: string): Uint8Array {
    return new TextEncoder().encode(`secureblob:v1|${blobId}|${scope}`)
}

// -- Helper: synchronous hashPage from noble -------------------------------

function buildHashPage(): HashPageFn {
    return (pageData: Uint8Array): PageHash => {
        const digest = nobleSha256(pageData)
        return Array.from(digest, b => b.toString(16).padStart(2, '0')).join('') as PageHash
    }
}

// -- sqlite3mt empty-root bootstrap ---------------------------------------

const EMPTY_HEAD_VERSION = 0n

function isBootstrapEmptyRoot(root: Root): boolean {
    return root.meta.pageCount === 0
        && root.meta.manifestRootId === ''
        && root.meta.parentPublicRootId == null
}

// -- Helper: build RPC bridge from workspace deps --------------------------

function resolveHeadTransport(
    deps: Sqlite3MtWorkspaceProviderDeps,
    workspace: string
): Sqlite3MtWorkspaceHeadTransport {
    return deps.resolveHeadTransport?.(workspace) ?? { headBlobId: workspace }
}

function resolveDurableStore(
    deps: Sqlite3MtWorkspaceProviderDeps,
    workspace: string,
    headTransport: Sqlite3MtWorkspaceHeadTransport
): DurableLocalStore {
    const durable = deps.createDurableLocalStore?.({ workspace, headTransport }) ?? deps.durable
    if (durable == null) {
        throw new Error(`sqlite3mt workspace provider requires a durable local store for ${headTransport.headBlobId}`)
    }
    return durable
}

/**
 * Construct a SecureBlobRpcBridge backed by BlobRepository + CryptoProvider.
 *
 * Blob access: repository.load() returns ciphertext; we decrypt with
 * stateKey + sqlite3mt-scoped AAD.
 *
 * Blob upload: encrypt plaintext, then repository.pushObjects().
 *
 * Public-head transport (rpcGetHeadVersioned, rpcPublishIfCurrent) is the
 * hardest integration seam. It bridges the sqlite3mt Root/version model to
 * SecureBlob's signed commit + CAS system:
 *
 *   getHead:  getHeads(blobId, lane) → load commit → extractVersion → load state → decrypt Root
 *   publish:  serialize Root → encrypt → buildSignedCommit → push → updateHead CAS
 *
 * Reads only need the head carrier so they can decode the current root/version.
 * Publish still requires writerSeed from the head transport for commit signing.
 * Publish correctness is driven by the explicit expected Root/version pair.
 * When the exact current head commit was already decoded in this runtime, the
 * bridge reuses it directly instead of forcing another /heads round-trip.
 */
function buildRpcBridge(
    deps: Sqlite3MtWorkspaceProviderDeps,
    headTransport: Sqlite3MtWorkspaceHeadTransport,
    durable: Pick<DurableLocalStore, 'getBlob' | 'putBlob' | 'getManifestNode' | 'putManifestNode'>
): SecureBlobRpcBridge {
    const { repository, crypto, stateKey, blobId } = deps
    const aad = sqlite3mtAad(blobId, 'sqlite3mt')
    const pageSize = deps.pageSize ?? 4096

    const publishUnsupportedMessage =
        headTransport.writerSeed == null
            ? `sqlite3mt workspace provider public-head publish for "${headTransport.headBlobId}" ` +
              'requires writerSeed for commit signing'
            : null
    const debug = { stage: null as string | null }
    const setDebugStage = (stage: string | null): void => {
        debug.stage = stage
    }
    const logTransport = (message: string, extra?: Record<string, unknown>): void => {
        sdkConsole.trace('[sqlite3mt:transport-debug]', {
            headBlobId: headTransport.headBlobId,
            stage: debug.stage,
            message,
            ...extra,
        })
    }
    type DecodedHeadCommit = {
        commitHash: string
        root: Root
        version: bigint
        writers: CommitRecord['writers']
    }
    let cachedHeadCommit: DecodedHeadCommit | null = null

    const cacheDecodedHeadCommit = (head: DecodedHeadCommit): DecodedHeadCommit => {
        cachedHeadCommit = head
        return head
    }

    const persistDurableBlobBestEffort = (hash: string, bytes: Uint8Array): void => {
        void durable.putBlob(hash as PageHash, bytes).catch(error => {
            sdkConsole.warn('[sqlite3mt:transport] rpcGetBlob:cache-error', { hash, error })
        })
    }

    const persistDurableManifestNodeBestEffort = (hash: string, bytes: Uint8Array): void => {
        void durable.putManifestNode(hash, bytes).catch(error => {
            sdkConsole.warn('[sqlite3mt:transport] rpcGetManifestNode:cache-error', { hash, error })
        })
    }

    const loadDurablePageBlob = async (hash: string, stagePrefix: string): Promise<Uint8Array | null> => {
        setDebugStage(`${stagePrefix}:${hash}:durable-load`)
        const bytes = await durable.getBlob(hash as PageHash)
        if (bytes != null) {
            sdkConsole.trace('[sqlite3mt:transport] durable page hit', { hash, bytes: bytes.byteLength })
        }
        return bytes
    }

    const loadDurableManifestNode = async (hash: string, stagePrefix: string): Promise<Uint8Array | null> => {
        setDebugStage(`${stagePrefix}:${hash}:durable-load`)
        const bytes = await durable.getManifestNode(hash)
        if (bytes != null) {
            sdkConsole.trace('[sqlite3mt:transport] durable manifest hit', { hash, bytes: bytes.byteLength })
        }
        return bytes
    }

    const decryptAndCacheRemotePageBlob = async (
        hash: string,
        ciphertext: Uint8Array,
        stagePrefix: 'get-blob' | 'get-blobs',
    ): Promise<Uint8Array> => {
        setDebugStage(`${stagePrefix}:${hash}:decrypt`)
        const plaintext = await crypto.decrypt(stateKey, ciphertext, aad)
        const plaintextHash = await crypto.sha256(plaintext)
        if (plaintextHash !== hash) {
            throw new Error(`sqlite3mt: remote blob plaintext hash mismatch for ${hash}`)
        }
        setDebugStage(`${stagePrefix}:${hash}:cache`)
        persistDurableBlobBestEffort(hash, plaintext)
        return plaintext
    }

    const cachedHeadMatches = (root: Root, version: bigint): boolean =>
        cachedHeadCommit != null
        && cachedHeadCommit.version === version
        && sameRoot(cachedHeadCommit.root, root)

    const decodeHeadCommit = async (
        commitHash: string
    ): Promise<DecodedHeadCommit> => {
        if (cachedHeadCommit?.commitHash === commitHash) {
            logTransport('decodeHeadCommit:cache-hit', { commitHash })
            return cachedHeadCommit
        }

        logTransport('decodeHeadCommit:start', { commitHash })
        setDebugStage(`decode-head:${commitHash}:load-commit`)
        // BlobRepository.load() already applies bounded local/remote waits and
        // coalesces inflight loads. Wrapping it in an extra timeout here can
        // abandon the shared load promise while BlobRepository still keeps it
        // inflight, causing every retry to rejoin the same zombie promise.
        const commitBytes = await repository.load(commitHash)
        if (commitBytes == null) {
            throw new Error(
                `sqlite3mt: remote head commit ${commitHash} not loadable`
            )
        }
        logTransport('decodeHeadCommit:commit-loaded', {
            commitHash,
            bytes: commitBytes.byteLength,
        })

        setDebugStage(`decode-head:${commitHash}:extract-version`)
        const version = extractVersionFromCommit(commitBytes)
        if (version == null) {
            throw new Error(
                `sqlite3mt: head commit ${commitHash} has no version field`
            )
        }
        logTransport('decodeHeadCommit:version-extracted', {
            commitHash,
            version: version.toString(),
        })

        setDebugStage(`decode-head:${commitHash}:parse-commit`)
        const record = JSON.parse(
            new TextDecoder().decode(commitBytes)
        ) as CommitRecord
        const payload = record.payload as Record<string, unknown>
        logTransport('decodeHeadCommit:commit-parsed', {
            commitHash,
            payloadKind: payload.kind,
        })
        setDebugStage(`decode-head:${commitHash}:decode-payload`)
        const { root } = await decodePublicRootV2Payload(payload, {
            sha256: crypto.sha256,
            expectedPageSize: pageSize,
        })
        logTransport('decodeHeadCommit:done', {
            commitHash,
            version,
            rootId: root.rootId,
        })

        return cacheDecodedHeadCommit({
            commitHash,
            root,
            version,
            writers: record.writers ?? [],
        })
    }

    const loadCurrentHead = async (
        expectedRoot?: Root,
        expectedVersion?: bigint
    ): Promise<DecodedHeadCommit> => {
        if (
            expectedRoot != null
            && expectedVersion != null
            && cachedHeadMatches(expectedRoot, expectedVersion)
        ) {
            setDebugStage(`load-current-head:cached:${cachedHeadCommit!.commitHash}`)
            return cachedHeadCommit!
        }

        assertSqlite3MtSyncAllowed(deps, 'load-current-head')
        setDebugStage('load-current-head:get-heads')
        const { heads, prefetchHints } = await withSqlite3MtTransportTimeout(
            repository.getHeads(blobId, headTransport.headLane),
            `load current head list for ${headTransport.headBlobId}`,
        )
        if (prefetchHints?.length) {
            repository.prefetch(prefetchHints)
        }
        if (heads.length === 0) {
            throw new Error(
                `sqlite3mt: no remote head for blobId "${headTransport.headBlobId}"`
            )
        }

        setDebugStage(`load-current-head:decode:${heads[0]!}`)
        return decodeHeadCommit(heads[0]!)
    }

    const rpcGetBlobsBatch = async (hashes: string[]): Promise<Map<string, Uint8Array>> => {
        const hashList = Array.from(new Set(hashes.filter(hash => hash.length > 0)))
        const result = new Map<string, Uint8Array>()
        if (hashList.length === 0) {
            return result
        }

        setDebugStage(`get-blobs:${hashList.length}:durable-load`)
        const durableHits = await Promise.all(hashList.map(async hash => {
            const local = await loadDurablePageBlob(hash, 'get-blobs')
            return { hash, local }
        }))
        const remoteMisses: string[] = []
        for (const { hash, local } of durableHits) {
            if (local != null) result.set(hash, local)
            else remoteMisses.push(hash)
        }

        if (remoteMisses.length === 0) {
            sdkConsole.trace('[sqlite3mt:transport] rpcGetBlobs:local-ok', {
                requested: hashList.length,
            })
            return result
        }

        setDebugStage(`get-blobs:${remoteMisses.length}:load-batch`)
        sdkConsole.trace('[sqlite3mt:transport] rpcGetBlobs:start', {
            count: remoteMisses.length,
            hashes: remoteMisses,
            localHits: result.size,
        })
        const ciphertextByHash = typeof repository.loadBatchStrict === 'function'
            ? await repository.loadBatchStrict(remoteMisses)
            : await repository.loadBatch(remoteMisses)

        for (const hash of remoteMisses) {
            const ciphertext = ciphertextByHash.get(hash)
            if (ciphertext == null) {
                continue
            }

            const plaintext = await decryptAndCacheRemotePageBlob(hash, ciphertext, 'get-blobs')
            result.set(hash, plaintext)
        }

        sdkConsole.trace('[sqlite3mt:transport] rpcGetBlobs:ok', {
            requested: hashList.length,
            found: result.size,
        })
        return result
    }

    return {
        getDebugStage() {
            return debug.stage
        },
        getDebugDetails() {
            return {
                repository: (repository as { getDebugState?: () => unknown }).getDebugState?.() ?? null,
            }
        },
        async rpcGetHeadVersioned(): Promise<{ root: Root; version: bigint }> {
            assertSqlite3MtSyncAllowed(deps, 'get-head')
            const { heads, prefetchHints } = await withSqlite3MtTransportTimeout(
                repository.getHeads(blobId, headTransport.headLane),
                `get head list for ${headTransport.headBlobId}`,
            )
            if (prefetchHints?.length) {
                repository.prefetch(prefetchHints)
            }
            if (heads.length === 0) {
                const emptyRoot = await createEmptyPublicRoot({
                    pageSize,
                    sha256: crypto.sha256,
                })
                sdkConsole.trace('[sqlite3mt:transport] rpcGetHeadVersioned synthetic-empty', {
                    headBlobId: headTransport.headBlobId,
                })
                return { root: emptyRoot, version: EMPTY_HEAD_VERSION }
            }
            const currentHead = await decodeHeadCommit(heads[0]!)
            sdkConsole.trace('[sqlite3mt:transport] rpcGetHeadVersioned current-head', {
                headBlobId: headTransport.headBlobId,
                commitHash: currentHead.commitHash,
                version: currentHead.version.toString(),
                pageCount: currentHead.root.meta.pageCount,
            })
            return { root: currentHead.root, version: currentHead.version }
        },

        async rpcPublishIfCurrent(
            expectedRoot: Root,
            expectedVersion: bigint,
            nextRoot: Root,
            publishPrefetchHints?: string[]
        ): Promise<PublishResult> {
            if (publishUnsupportedMessage) throw new Error(publishUnsupportedMessage)
            assertSqlite3MtSyncAllowed(deps, 'publish')
            const writerSeed = headTransport.writerSeed!
            setDebugStage('publish:start')
            sdkConsole.debug('[sqlite3mt:transport] rpcPublishIfCurrent:start', {
                expectedRootId: expectedRoot.rootId,
                expectedVersion: expectedVersion.toString(),
                nextRootId: nextRoot.rootId,
            })

            // First-ever publish: from local empty-root bootstrap, no existing head.
            if (isBootstrapEmptyRoot(expectedRoot) && expectedVersion === EMPTY_HEAD_VERSION) {
                setDebugStage('publish:first:get-heads')
                sdkConsole.debug('[sqlite3mt:transport] rpcPublishIfCurrent first-publish:start', {
                    headBlobId: headTransport.headBlobId,
                    nextRootId: nextRoot.rootId,
                    nextPageCount: nextRoot.meta.pageCount,
                })
                const { heads, prefetchHints } = await withSqlite3MtTransportTimeout(
                    repository.getHeads(blobId, headTransport.headLane),
                    `first publish head list for ${headTransport.headBlobId}`,
                )
                if (prefetchHints?.length) {
                    repository.prefetch(prefetchHints)
                }
                if (heads.length > 0) {
                    // Someone else published while we were preparing; return their head.
                    setDebugStage(`publish:first:raced:${heads[0]!}`)
                    const racedHead = await decodeHeadCommit(heads[0]!)
                    return {
                        ok: false,
                        currentRoot: racedHead.root,
                        currentVersion: racedHead.version,
                    }
                }

                const nextVersion = 1n
                const payload = await encodePublicRootV2PayloadFromRoot({
                    root: nextRoot,
                    pageSize,
                    version: nextVersion,
                    sha256: crypto.sha256,
                })
                setDebugStage('publish:first:sign-commit')
                const { commitBytes, commitHash } = await buildSignedCommit({
                    blobId,
                    parents: [],
                    payload,
                    writerSeed,
                    crypto,
                    additionalWriters: [],
                })

                setDebugStage('publish:first:push-objects')
                await withSqlite3MtTransportTimeout(
                    repository.pushObjects([{ hash: commitHash, data: commitBytes }]),
                    `upload commit ${commitHash}`,
                )
                setDebugStage('publish:first:verify-objects-visible')
                await withSqlite3MtTransportTimeout(
                    repository.ensureRemoteVisible([commitHash]),
                    `verify commit visibility ${commitHash}`,
                )

                setDebugStage('publish:first:update-head')
                const result = await withSqlite3MtTransportTimeout(
                    repository.updateHead(
                        blobId,
                        [],
                        commitHash,
                        undefined,
                        publishPrefetchHints,
                        headTransport.headLane
                    ),
                    `publish first head ${commitHash}`,
                )

                if (result.ok) {
                    const publishedCommit = JSON.parse(
                        new TextDecoder().decode(commitBytes)
                    ) as CommitRecord
                    cacheDecodedHeadCommit({
                        commitHash,
                        root: nextRoot,
                        version: nextVersion,
                        writers: publishedCommit.writers ?? [],
                    })
                    sdkConsole.debug('[sqlite3mt:transport] rpcPublishIfCurrent first-publish:committed', {
                        headBlobId: headTransport.headBlobId,
                        commitHash,
                        payloadKind: (payload as Sqlite3mtPublicRootV2Payload).kind,
                    })
                    sdkConsole.debug('[sqlite3mt:transport] rpcPublishIfCurrent:ok', {
                        nextRootId: nextRoot.rootId,
                        nextVersion: nextVersion.toString(),
                    })
                    return { ok: true }
                }

                // CAS race: another client published first.
                if (result.code === 'CAS_MISMATCH' && result.remoteHead) {
                    setDebugStage(`publish:first:cas-mismatch:${result.remoteHead}`)
                    const racedHead = await decodeHeadCommit(result.remoteHead)
                    return {
                        ok: false,
                        currentRoot: racedHead.root,
                        currentVersion: racedHead.version,
                    }
                }
                if (result.code === 'OBJECT_MISSING') {
                    throw sqlite3mtObjectMissingPublishError(result.message)
                }

                throw new Error(
                    `sqlite3mt: first CAS publish on ${headTransport.headBlobId} ` +
                    'failed and could not extract remote head'
                )
            }

            let currentHead: DecodedHeadCommit
            if (cachedHeadMatches(expectedRoot, expectedVersion)) {
                setDebugStage('publish:use-cached-head')
                logTransport('rpcPublishIfCurrent:use-cached-head', {
                    expectedVersion: expectedVersion.toString(),
                    expectedRootId: expectedRoot.rootId,
                })
                currentHead = cachedHeadCommit!
            } else {
                setDebugStage('publish:load-current-head')
                logTransport('rpcPublishIfCurrent:load-current-head', {
                    expectedVersion: expectedVersion.toString(),
                    expectedRootId: expectedRoot.rootId,
                })
                currentHead = await loadCurrentHead(expectedRoot, expectedVersion)
            }

            if (
                !sameRoot(currentHead.root, expectedRoot)
                || currentHead.version !== expectedVersion
            ) {
                setDebugStage('publish:expected-mismatch')
                sdkConsole.debug('[sqlite3mt:transport] rpcPublishIfCurrent:expected-mismatch-no-cas', {
                    headBlobId: headTransport.headBlobId,
                    expectedRootId: expectedRoot.rootId,
                    expectedVersion: expectedVersion.toString(),
                    currentRootId: currentHead.root.rootId,
                    currentVersion: currentHead.version.toString(),
                    currentCommitHash: currentHead.commitHash,
                })
                return {
                    ok: false,
                    currentRoot: currentHead.root,
                    currentVersion: currentHead.version,
                }
            }

            // 1. Build a signed commit with version = expectedVersion + 1
            const nextVersion = expectedVersion + 1n
            const payload = await encodePublicRootV2PayloadFromRoot({
                root: nextRoot,
                pageSize,
                version: nextVersion,
                sha256: crypto.sha256,
            })
            setDebugStage('publish:sign-commit')
            const { commitBytes, commitHash } = await buildSignedCommit({
                blobId,
                parents: [currentHead.commitHash],
                payload,
                writerSeed,
                crypto,
                additionalWriters: currentHead.writers,
            })

            // 2. Upload commit object
            setDebugStage('publish:push-objects')
            await withSqlite3MtTransportTimeout(
                repository.pushObjects([{ hash: commitHash, data: commitBytes }]),
                `upload commit ${commitHash}`,
            )
            setDebugStage('publish:verify-objects-visible')
            await withSqlite3MtTransportTimeout(
                repository.ensureRemoteVisible([commitHash]),
                `verify commit visibility ${commitHash}`,
            )

            // 3. CAS update head
            setDebugStage('publish:update-head')
            const result = await withSqlite3MtTransportTimeout(
                repository.updateHead(
                    blobId,
                    [currentHead.commitHash],
                    commitHash,
                    undefined,
                    publishPrefetchHints,
                    headTransport.headLane
                ),
                `publish head ${commitHash}`,
            )

            if (result.ok) {
                const publishedCommit = JSON.parse(
                    new TextDecoder().decode(commitBytes)
                ) as CommitRecord
                cacheDecodedHeadCommit({
                    commitHash,
                    root: nextRoot,
                    version: nextVersion,
                    writers: publishedCommit.writers ?? currentHead.writers,
                })
                sdkConsole.debug('[sqlite3mt:transport] rpcPublishIfCurrent:ok', {
                    nextRootId: nextRoot.rootId,
                    nextVersion: nextVersion.toString(),
                })
                return { ok: true }
            }

            // 5. CAS mismatch: extract current remote head's root + version
            if (result.code === 'CAS_MISMATCH' && result.remoteHead) {
                setDebugStage(`publish:cas-mismatch:${result.remoteHead}`)
                logTransport('rpcPublishIfCurrent:cas-mismatch:decode-start', {
                    remoteHead: result.remoteHead,
                    expectedVersion: expectedVersion.toString(),
                    expectedRootId: expectedRoot.rootId,
                })
                const remoteHead = await decodeHeadCommit(result.remoteHead)
                logTransport('rpcPublishIfCurrent:cas-mismatch:decode-done', {
                    remoteHead: result.remoteHead,
                    currentVersion: remoteHead.version.toString(),
                    currentRootId: remoteHead.root.rootId,
                })
                sdkConsole.warn('[sqlite3mt:transport] rpcPublishIfCurrent:cas-mismatch', {
                    currentRootId: remoteHead.root.rootId,
                    currentVersion: remoteHead.version.toString(),
                })
                return {
                    ok: false,
                    currentRoot: remoteHead.root,
                    currentVersion: remoteHead.version,
                }
            }
            if (result.code === 'OBJECT_MISSING') {
                throw sqlite3mtObjectMissingPublishError(result.message)
            }

            // Fallback: mismatch but can't extract remote head details
            throw new Error(
                `sqlite3mt: CAS mismatch on ${headTransport.headBlobId} ` +
                'and failed to extract remote head root/version'
            )
        },

        async rpcGetBlob(hash: string) {
            if (!hash) return null
            sdkConsole.trace('[sqlite3mt:transport] rpcGetBlob:start', { hash })
            const local = await loadDurablePageBlob(hash, 'get-blob')
            if (local != null) {
                return local
            }
            setDebugStage(`get-blob:${hash}:load`)
            const ciphertext = await repository.load(hash)
            if (ciphertext == null) return null
            const plaintext = await decryptAndCacheRemotePageBlob(hash, ciphertext, 'get-blob')
            if (plaintext == null) return null
            sdkConsole.trace('[sqlite3mt:transport] rpcGetBlob:ok', { hash, bytes: plaintext.byteLength })
            return plaintext
        },

        async rpcGetBlobs(hashes: string[]) {
            return rpcGetBlobsBatch(hashes)
        },

        async rpcPutMissingBlobs(hashes, loader) {
            const hashList = Array.from(new Set(hashes))
            sdkConsole.trace('[sqlite3mt:transport] rpcPutMissingBlobs:start', { count: hashList.length })
            setDebugStage(`put-blobs:missing:${hashList.length}`)
            const missingHashes = await repository.findMissingObjects(hashList)
            const toUpload: Array<{ hash: string; data: Uint8Array }> = []
            for (const hash of missingHashes) {
                setDebugStage(`put-blobs:${hash}:load-local`)
                const plaintext = await loader(hash)
                setDebugStage(`put-blobs:${hash}:encrypt`)
                const ciphertext = await crypto.encryptDeterministic(stateKey, plaintext, aad)
                toUpload.push({ hash, data: ciphertext })
            }
            if (toUpload.length > 0) {
                setDebugStage(`put-blobs:push:${toUpload.length}`)
                await withSqlite3MtTransportTimeout(
                    repository.pushObjects(toUpload, { force: true }),
                    `upload ${toUpload.length} page blob(s)`,
                )
                setDebugStage(`put-blobs:verify:${toUpload.length}`)
                await withSqlite3MtTransportTimeout(
                    repository.ensureRemoteVisible(toUpload.map(entry => entry.hash)),
                    `verify ${toUpload.length} page blob(s)`,
                )
            }
            sdkConsole.trace('[sqlite3mt:transport] rpcPutMissingBlobs:ok', {
                requested: hashList.length,
                uploaded: toUpload.length,
            })
        },

        async rpcGetManifestNode(hash) {
            if (!hash) return null
            const local = await loadDurableManifestNode(hash, 'get-manifest')
            if (local != null) {
                return local
            }
            setDebugStage(`get-manifest:${hash}:load`)
            sdkConsole.trace('[sqlite3mt:transport] rpcGetManifestNode:start', { hash })
            // §104 §5.2: manifest nodes are PUBLIC and server-readable.
            // They are stored as cleartext in the CAS, not encrypted.
            const bytes = await repository.load(hash)
            if (bytes == null) return null
            setDebugStage(`get-manifest:${hash}:cache`)
            persistDurableManifestNodeBestEffort(hash, bytes)
            sdkConsole.trace('[sqlite3mt:transport] rpcGetManifestNode:ok', { hash, bytes: bytes.byteLength })
            return bytes
        },

        async rpcGetManifestNodes(hashes) {
            const hashList = Array.from(new Set(hashes.filter(Boolean)))
            const result = new Map<string, Uint8Array>()
            if (hashList.length === 0) return result

            const remoteMisses: string[] = []
            for (const hash of hashList) {
                const local = await loadDurableManifestNode(hash, 'get-manifests')
                if (local != null) {
                    result.set(hash, local)
                } else {
                    remoteMisses.push(hash)
                }
            }
            if (remoteMisses.length === 0) return result

            setDebugStage(`get-manifests:${remoteMisses.length}:load-batch`)
            sdkConsole.trace('[sqlite3mt:transport] rpcGetManifestNodes:start', {
                requested: hashList.length,
                remoteMisses: remoteMisses.length,
            })
            const remote = await repository.loadBatch(remoteMisses)
            for (const [hash, bytes] of remote) {
                result.set(hash, bytes)
                persistDurableManifestNodeBestEffort(hash, bytes)
            }
            sdkConsole.trace('[sqlite3mt:transport] rpcGetManifestNodes:ok', {
                requested: hashList.length,
                found: result.size,
            })
            return result
        },

        async rpcPutMissingManifestNodes(hashes, loader) {
            const hashList = Array.from(new Set(hashes))
            sdkConsole.trace('[sqlite3mt:transport] rpcPutMissingManifestNodes:start', { count: hashList.length })
            const toUpload: Array<{ hash: string; data: Uint8Array }> = []
            for (const hash of hashList) {
                setDebugStage(`put-manifest:${hash}:load-local`)
                const plaintext = await loader(hash)
                // §104 §5.2: manifest nodes are PUBLIC — upload as cleartext.
                // No encryption. The server reads manifest nodes directly for GC.
                toUpload.push({ hash, data: plaintext })
            }
            if (toUpload.length > 0) {
                setDebugStage(`put-manifest:push:${toUpload.length}`)
                sdkConsole.trace('[sqlite3mt:transport] rpcPutMissingManifestNodes:push', {
                    count: toUpload.length,
                    hashes: hashList,
                })
                await withSqlite3MtTransportTimeout(
                    repository.pushObjects(toUpload),
                    `upload ${toUpload.length} manifest node(s)`,
                )
                setDebugStage(`put-manifest:verify:${toUpload.length}`)
                sdkConsole.trace('[sqlite3mt:transport] rpcPutMissingManifestNodes:verify', {
                    count: toUpload.length,
                    hashes: hashList,
                })
                await withSqlite3MtTransportTimeout(
                    repository.ensureRemoteVisible(hashList),
                    `verify ${toUpload.length} manifest node(s)`,
                )
                sdkConsole.trace('[sqlite3mt:transport] rpcPutMissingManifestNodes:verified', {
                    count: toUpload.length,
                    hashes: hashList,
                })
            }
            sdkConsole.trace('[sqlite3mt:transport] rpcPutMissingManifestNodes:ok', {
                requested: hashList.length,
                uploaded: toUpload.length,
            })
        },
    }
}

// -- Helper: build manifest ops deps from workspace deps -------------------

function buildManifestOpsDeps(
    deps: Sqlite3MtWorkspaceProviderDeps,
    rpc: SecureBlobRpcBridge,
    durable: DurableLocalStore,
): ManifestOpsDeps {
    const { crypto, repository } = deps

    return {
        async loadManifestNode(hash) {
            const plaintext = await durable.getManifestNode(hash) ?? await rpc.rpcGetManifestNode(hash)
            if (plaintext == null) return null
            try {
                const parsed = JSON.parse(new TextDecoder().decode(plaintext))
                // Validate it's a v2 manifest node
                if (parsed && parsed.type === 'sqlite3mt-manifest-v2') {
                    return parsed as import('./sqlite3mt/public-manifest').Sqlite3mtManifestV2Node
                }
                return null
            } catch {
                return null
            }
        },

        async loadPageBlob(hash) {
            return await durable.getBlob(hash as PageHash) ?? await rpc.rpcGetBlob(hash)
        },

        async sha256(data) {
            return crypto.sha256(data)
        },

        hashPage: buildHashPage(),

        async hasObject(hash) {
            const existing = await withSqlite3MtTransportTimeout(
                repository.load(hash),
                `probe object ${hash}`,
            )
            return existing != null
        },

        blockSize: deps.pageSize ?? 4096,
    }
}

// -- Main factory ----------------------------------------------------------

/**
 * Create a workspace-level sqlite3mt engine factory provider.
 *
 * This function returns a `createSqliteEngineFactory` callback suitable for
 * passing to `SdkConfig.createSqliteEngineFactory`. When invoked by the
 * session layer, it constructs the full sqlite3mt runtime bridge from the
 * workspace-level deps.
 */
export function createSqlite3MtWorkspaceProvider(
    deps: Sqlite3MtWorkspaceProviderDeps
): (context: WorkspaceSqliteEngineFactoryContext) => Promise<SqliteEngineFactory> {
    return async (context: WorkspaceSqliteEngineFactoryContext) => {
        const hashPage = buildHashPage()

        const createRuntime = (args: CreateSqlite3MtRuntimeArgs) => {
            emitSecureBlobComponentProgress('sqlite3mt-workspace', 'Resolving sqlite3mt workspace runtime', {
                workspace: args.config.workspace,
                virtualRuntimeOwner: deps.virtualRuntimeOwner === true,
            })
            const headTransport = resolveHeadTransport(deps, args.config.workspace)
            emitSecureBlobComponentProgress('sqlite3mt-workspace', 'Resolved sqlite3mt head transport', {
                headBlobId: headTransport.headBlobId,
                headLane: headTransport.headLane ?? null,
            })
            const durable = resolveDurableStore(deps, args.config.workspace, headTransport)
            emitSecureBlobComponentProgress('sqlite3mt-workspace', 'Resolved sqlite3mt durable store')
            const rpc = buildRpcBridge(deps, headTransport, durable)
            const manifestOpsDeps = buildManifestOpsDeps(deps, rpc, durable)
            const manifestOps: FullManifestOperations = buildManifestOperations(manifestOpsDeps)
            emitSecureBlobComponentProgress('sqlite3mt-workspace', 'Built sqlite3mt RPC and manifest bridges')
            const lifecycleDeps: Sqlite3MtLifecycleRuntimeBridgeDeps = {
                durable,
                manifest: manifestOps,
                rpc,
                hashPage,
                faultReadAheadPages: deps.faultReadAheadPages,
                observers: deps.observers,
            }
            emitSecureBlobComponentProgress('sqlite3mt-workspace', 'Creating sqlite3mt lifecycle runtime bridge')
            const bridgePromise = createSqlite3MtLifecycleRuntimeBridge(args, lifecycleDeps)

            return bridgePromise.then((bridge): Sqlite3MtRuntimeBridge => {
                emitSecureBlobComponentProgress('sqlite3mt-workspace', 'sqlite3mt lifecycle runtime bridge ready', {
                    runtimeEpoch: bridge.runtimeEpoch,
                    dbName: bridge.dbName,
                })
                const unregisterSyncHook = deps.onSyncEngineReady?.(
                    headTransport.headBlobId,
                    () => {
                        if (isWorkspaceSyncPaused(deps)) return
                        void bridge.databaseAdapter?.syncNow().catch(err => {
                            sdkConsole.warn('[sqlite3mt] WS-triggered sync failed:', err)
                        })
                    },
                    { backgroundSync: args.config.backgroundSync },
                )
                let disposed = false

                return {
                    get runtimeEpoch() {
                        return bridge.runtimeEpoch
                    },
                    get databaseAdapter() {
                        return bridge.databaseAdapter
                    },
                    get dbName() {
                        return bridge.dbName
                    },
                    hashPage(pageData) {
                        return bridge.hashPage(pageData)
                    },
                    createHandleHooks(runtimeContext) {
                        return bridge.createHandleHooks(runtimeContext)
                    },
                    postFaultMessage(conn, msg) {
                        bridge.postFaultMessage(conn, msg)
                    },
                    postCommitSyncMessage(conn, msg, transfer) {
                        bridge.postCommitSyncMessage(conn, msg, transfer)
                    },
                    enterFatalIoMode(conn) {
                        bridge.enterFatalIoMode(conn)
                    },
                    handleExternalMessage(message) {
                        bridge.handleExternalMessage?.(message)
                    },
                    enterExternalClientIoFatal(connId) {
                        bridge.enterExternalClientIoFatal?.(connId)
                    },
                    enterExternalClientRequest(connId, runtimeEpoch) {
                        bridge.enterExternalClientRequest?.(connId, runtimeEpoch)
                    },
                    leaveExternalClientRequest(connId, runtimeEpoch) {
                        bridge.leaveExternalClientRequest?.(connId, runtimeEpoch)
                    },
                    get runWalCheckpoint() {
                        return bridge.runWalCheckpoint
                    },
                    set runWalCheckpoint(runWalCheckpoint) {
                        bridge.runWalCheckpoint = runWalCheckpoint
                    },
                    async dispose() {
                        if (disposed) {
                            return
                        }

                        disposed = true
                        try {
                            if (typeof unregisterSyncHook === 'function') {
                                unregisterSyncHook()
                            }
                        } finally {
                            try {
                                await bridge.dispose()
                            } finally {
                                const maybeClose = (durable as { close?: (() => Promise<void> | void) } | null)?.close
                                if (typeof maybeClose === 'function') {
                                    await maybeClose.call(durable)
                                }
                            }
                        }
                    },
                }
            })
        }

        return createSqlite3MtEngineFactory({
            loadSqlite3: deps.loadSqlite3,
            createRuntime,
            virtualRuntimeOwner: deps.virtualRuntimeOwner,
            initialWalSize: deps.initialWalSize,
            maxWalSize: deps.maxWalSize,
            logging: deps.logging,
        })
    }
}
